'use client';

import { ThreadPrimitive } from '@assistant-ui/react';
import { type JSX } from 'react';

import { useChatSessionContext } from '../../_context/SessionContext';

type Suggestion = {
    readonly title: string;
    readonly label: string;
    readonly prompt: string;
};

export const SUGGESTIONS: readonly Suggestion[] = [
    {
        title: 'Dogs playing outdoors',
        label: 'for a pet video',
        prompt: 'Find videos of dogs playing outdoors, various breeds running and having fun',
    },
    {
        title: 'Cinematic mountain footage',
        label: 'for a documentary',
        prompt: 'Find cinematic mountain footage for a documentary, preferably 4K with dramatic lighting',
    },
    {
        title: 'Time-lapse cityscapes at night',
        label: 'for a travel vlog',
        prompt: 'Show me time-lapse videos of city skylines at night, with moving lights and modern buildings',
    },
] as const;

// Shown once audio search is enabled: the dog example is swapped for a music
// prompt so the empty state demonstrates that audio/music can be searched too.
export const AUDIO_SUGGESTIONS: readonly Suggestion[] = [
    {
        title: 'Energetic music tracks',
        label: 'for a gym promo video',
        prompt: 'Find energetic music tracks for a gym promo video',
    },
    {
        title: 'Cinematic mountain footage',
        label: 'for a documentary',
        prompt: 'Find cinematic mountain footage for a documentary, preferably 4K with dramatic lighting',
    },
    {
        title: 'Time-lapse cityscapes at night',
        label: 'for a travel vlog',
        prompt: 'Show me time-lapse videos of city skylines at night, with moving lights and modern buildings',
    },
] as const;

/**
 * Initial suggestions shown in the welcome screen.
 */
function ThreadSuggestions({ suggestions }: { suggestions: readonly Suggestion[] }): JSX.Element {
    return (
        <div className="space-y-2">
            {suggestions.map((suggestion) => (
                <ThreadPrimitive.Suggestion key={suggestion.prompt} prompt={suggestion.prompt} send asChild>
                    <button
                        type="button"
                        className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all group"
                        aria-label={suggestion.prompt}
                    >
                        <span className="block font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                            {suggestion.title}
                        </span>
                        <span className="block text-sm text-gray-500">{suggestion.label}</span>
                    </button>
                </ThreadPrimitive.Suggestion>
            ))}
        </div>
    );
}

/**
 * Welcome message shown when the thread is empty.
 */
export function ThreadWelcome(): JSX.Element {
    const { supportsAudio } = useChatSessionContext();

    const suggestions = supportsAudio ? AUDIO_SUGGESTIONS : SUGGESTIONS;
    const description = supportsAudio
        ? 'Describe the video or audio you need in natural language.'
        : 'Describe the footage or templates you need in natural language.';

    return (
        <div className="flex flex-col h-full px-4 pt-8">
            <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">What are you looking for?</h2>
                <p className="text-sm text-gray-500">{description}</p>
            </div>
            <ThreadSuggestions suggestions={suggestions} />
        </div>
    );
}
